import { useEffect, useState } from "react";
import { Form } from "antd";
import { CopyClipBoardBlack, GreenTickCircle, WarningIcon } from "assets/icons";
import Button from "components/BaseWidgets/button";
import Input from "components/BaseWidgets/form/input";
import Modal from "components/BaseWidgets/modal";
import AlertNotification from "components/CustomWidgets/alertNotification";
import {
  useCreateVerifyPayment,
  usePaymentVerifyCallback,
  usePaymentVerifyStatus,
  usePhysicalVerification,
  useRequestNewPincode,
  useVerifyPincode,
} from "hooks/api/useAccountVerify";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { retrieveToken, storeToken } from "utils/storage";

function PhysicalVerificationModal({ open, setOpen, amount }) {
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [showIcon, setShowIcon] = useState(false);
  const [status, setStatus] = useState(null); // null, "failed", or "success"
  const [verificationCode, setVerificationCode] = useState("");
  const [isPincodeExists, setIsPincodeExists] = useState(null);
  const [canPay, setCanPay] = useState(false);

  const queryClient = useQueryClient();
  const baseurl = import.meta.env.VITE_REDIRECT;
  const txnid = searchParams.get("txnid");
  const navigate = useNavigate();

  const { mutate: requestPhysicalVerification, isLoading } =
    usePhysicalVerification();

  const resetState = () => {
    setOpen(false);
    setCanPay(false);
    form.resetFields();
    setShowIcon(false);
    setStatus(null);
  };

  // payment functions
  const { mutate: createPayment, isLoading: paymentLoading } =
    useCreateVerifyPayment({
      onSuccess: (data) => {
        const paymentDetails = data?.payment_data;

        if (!paymentDetails?.url || !paymentDetails?.payload) {
          console.error("Payment details missing!");
          return;
        }

        const { url, payload } = paymentDetails;

        const form = document.createElement("form");
        form.method = "POST";
        form.action = url;

        const orderedFields = {
          key: payload.key,
          txnid: payload.txnid,
          amount: payload.amount,
          productinfo: payload.productinfo,
          firstname: payload.firstname,
          email: payload.email,
          phone: payload.phone,
          lastname: payload.lastname || "",
          address1: payload.address1 || "",
          surl: payload.surl,
          furl: payload.furl,
          hash: payload.hash,
          service_provider: payload.service_provider || "payu_paisa",
          udf1: "",
          udf2: "",
          udf3: "",
          udf4: "",
          udf5: "",
        };
        Object.entries(orderedFields).forEach(([k, v]) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = k;
          input.value = v ?? "";
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
      },
    });

  const { isLoading: loading } = usePaymentVerifyCallback(txnid, {
    enabled: Boolean(txnid),
  });

  const { mutate: fetchPaymentStatus } = usePaymentVerifyStatus({
    onSuccess: (data) => {
      const transactionData = data?.transaction_details[txnid];
      if (transactionData?.status === "success") {
        const pinCode = retrieveToken("pinCode");
        requestPhysicalVerification(
          { pincode: pinCode },
          {
            onSuccess: (data) => {
              const verificationCode = data?.verification_code || code;
              setVerificationCode(verificationCode);
              setStatus("success");
              setShowIcon(true);
              queryClient.invalidateQueries(["verificationDetails"]);
            },
            onError: () => {
              setStatus("failed");
            },
          },
        );
      } else if (transactionData?.status === "failed") {
        AlertNotification("error", transactionData?.error_Message);
      } else {
        AlertNotification("error", transactionData?.error_Message);
      }
    },
  });

  useEffect(() => {
    if (txnid) {
      fetchPaymentStatus({ txnid });
    }
  }, [txnid, fetchPaymentStatus]);

  const onFinish = ({ pinCode }) => {
    storeToken("pinCode", pinCode);
    createPayment({
      pincode: Number(pinCode),
    });
  };

  const { mutate: checkPincode, isLoading: checkingPincode } = useVerifyPincode(
    {
      onSuccess: (data) => {
        const exists = data?.data?.pincode_exists;

        setIsPincodeExists(exists);

        if (exists) {
          setStatus(null);
          setCanPay(true);
        } else {
          setStatus("failed");
          setCanPay(false);
        }
      },
      onError: (error) => {
        AlertNotification("error", error.message || "Failed to verify pincode");
      },
    },
  );

  const { mutate: requestNewPincode, isLoading: requestingNew } =
    useRequestNewPincode({
      onMutate: () => {
        resetState();
      },
      onSuccess: () => {
        setStatus("success");
        setCanPay(true);
        AlertNotification("success", "Pincode request successful");
      },
      onError: (error) => {
        AlertNotification(
          "error",
          error.message || "Failed to request new pincode",
        );
      },
    });

  const modalTexts = {
    title: "Physical verification",
    description:
      status === "failed"
        ? "Select a listed Pincode for physical verification, or submit a new one if yours isn’t available, and receive a confirmation with a unique verification code."
        : "Enter your PIN code for physical verification, or submit a new one if yours isn’t available, and receive a confirmation with a unique verification code.",
  };

  const renderContent = () => {
    if (status === "failed") {
      return (
        <div className="flex items-start gap-2">
          <WarningIcon />
          <p className="warning-text">
            We couldn't verify your PIN code. Please double-check and try again,
            or request a new verification to receive an updated code.
          </p>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3 mt-2">
        <p className="verification-text">
          A verification fee applies to process and confirm your details.
        </p>
        <div className="flex items-center gap-2">
          <p className="verification-text">Verification fee:</p>
          <span className="amount-text">₹{amount}</span>
        </div>
      </div>
    );
  };

  return (
    <Modal
      wrapClassName="document-upload-modal"
      isOpen={open}
      onCancel={() => {
        resetState();
        navigate("/verification", { replace: true });
      }}
      title={
        <div className="flex flex-col gap-1">
          <span className="font-title-lg fw-700 leading-3">
            {modalTexts.title}
          </span>
          <p className="font-body-md2 leading-5">{modalTexts.description}</p>
        </div>
      }
      closable
      width={600}
      footer={
        <div className="flex justify-end gap-3 items-center">
          <Button variant="secondary" height={44} onClick={resetState}>
            Cancel
          </Button>

          {status === "failed" ? (
            <Button
              height={44}
              loading={requestingNew}
              onClick={() => {
                requestNewPincode({ pincode: form.getFieldValue("pinCode") });
                resetState();
              }}
            >
              Request new
            </Button>
          ) : (
            <Button
              height={44}
              htmlType="submit"
              form="pin-form"
              loading={isLoading || paymentLoading}
              disabled={!canPay}
            >
              Pay now
            </Button>
          )}
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        id="pin-form"
        className="flex flex-col gap-6"
      >
        <Form.Item
          name="pinCode"
          className="custom-error pin-code-input"
          label="Pin code"
          rules={[
            { required: true, message: "Please enter your pin code" },
            {
              pattern: /^\d{6}$/,
              message: "Pin code must be a 6-digits number",
            },
          ]}
        >
          <Input
            type="number"
            placeholder="Enter pin code"
            maxLength={6}
            icon={isPincodeExists ? <GreenTickCircle /> : null}
            onChange={(e) => {
              const value = e.target.value;
              setShowIcon(/^\d{6}$/.test(value));

              if (/^\d{6}$/.test(value)) {
                checkPincode(value);
              } else {
                setIsPincodeExists(null);
                setStatus(null);
                setCanPay(false);
              }
            }}
            disabled={status === "success"}
            className={status !== "success" ? "input-container" : ""}
          />
        </Form.Item>

        {renderContent()}
      </Form>
    </Modal>
  );
}

export default PhysicalVerificationModal;
